import { combineReducers, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { Option, Record as EffectRecord } from 'effect'

import type { SessionChat } from '../domain/sessionChat'
import { agentChatSlice } from './agentChat'
import { agentTargetsSlice, chatTargetOf } from './agentTargets'
import { sessionIdAssigned } from './pendingId'

// ウィジェットの状態。会話、開いているチャット、会話の対象の slice を1つの reducer にまとめ、AgentWidget の useReducer に渡す
// （salescore-agent-docs の arch/frontend/specification.md の「状態の管理」、ADR-104）。
// slice 同士が action を受け合う（セッションを作ったら、会話、開いているチャット、会話の対象を付け替える）ので、reducer を1つにする

interface OpenChatState {
  // 開いているチャット。新しいチャット（まだ指示を送っていない）なら null、セッションを作っている間は仮の ID
  openSessionId: string | null
  // 別の会話に切り替えた回数。会話の表示の key にして、切り替えるたびに作り直す（表示は「上に戻って読んでいるか」を覚えていて、
  // 作り直さないと前の会話の値が残る）。チャットを切り替える action はすべてこれを増やす。仮の ID からの付け替えでは増やさない
  // （付け替えで作り直すと、「考え中」の待ち時間が数え直しになる）
  openedChatCount: number
}

const INITIAL_OPEN_CHAT_STATE: OpenChatState = { openSessionId: null, openedChatCount: 0 }

// 表示のモードとパネルの大きさは、#10110 のとおり AgentWidget の中に別に持つ
const agentOpenChatSlice = createSlice({
  name: 'agentOpenChat',
  initialState: INITIAL_OPEN_CHAT_STATE,
  reducers: {
    // 指示を送るチャットを開く。新しいチャットの最初の指示では、仮の ID で開く
    chatOpened: (state, action: PayloadAction<{ agentSessionId: string }>) => ({
      ...state,
      openSessionId: action.payload.agentSessionId,
    }),
    // 新しいチャットを始める。前のチャットの実行中のターンは止めない
    newChatStarted: (state) => ({ openSessionId: null, openedChatCount: state.openedChatCount + 1 }),
  },
  extraReducers: (builder) => {
    builder.addCase(sessionIdAssigned, (state, action) =>
      state.openSessionId === action.payload.pendingSessionId
        ? { ...state, openSessionId: action.payload.agentSessionId }
        : state,
    )
  },
})

export const { chatOpened, newChatStarted } = agentOpenChatSlice.actions

export const agentWidgetReducer = combineReducers({
  chat: agentChatSlice.reducer,
  openChat: agentOpenChatSlice.reducer,
  targets: agentTargetsSlice.reducer,
})

export type AgentWidgetState = ReturnType<typeof agentWidgetReducer>

// useReducer の初期値。各 slice の initialState から作る
export const initialAgentWidgetState = (): AgentWidgetState =>
  agentWidgetReducer(undefined, { type: 'agentWidget/initialized' })

// 開いているチャット。新しいチャット（まだ指示を送っていない）なら null
export const selectOpenSessionId = (state: AgentWidgetState): string | null => state.openChat.openSessionId

// 始め直すもの（指示を送ったチャット）があるか
export const selectCanStartNewChat = (state: AgentWidgetState): boolean => selectOpenSessionId(state) !== null

// 別の会話に切り替えた回数。会話の表示の key にする
export const selectOpenedChatCount = (state: AgentWidgetState): number => state.openChat.openedChatCount

// 開いているチャットの会話。まだ指示を送っていなければ undefined
export const selectOpenSessionChat = (state: AgentWidgetState): SessionChat | undefined => {
  const openSessionId = selectOpenSessionId(state)
  return openSessionId === null ? undefined : Option.getOrUndefined(EffectRecord.get(state.chat, openSessionId))
}

// 停止ボタンと入口の実行中表示が、会話全体を購読せずに参照するターン。
export const selectOpenRunningTurnId = (state: AgentWidgetState): string | null =>
  selectOpenSessionChat(state)?.runningTurnId ?? null

// 開いているチャットで、停止を押してターンの終わりを待っているか。停止のボタンを「停止中」にする
export const selectIsOpenTurnStopping = (state: AgentWidgetState): boolean => {
  const session = selectOpenSessionChat(state)
  return session !== undefined && session.stoppingTurnId !== null && session.stoppingTurnId === session.runningTurnId
}

// 開いているチャットに今指示を送れるか。実行中は送らない（ADR-107）。送信の操作は ref に、部品は描いた状態に当てて、同じ判定を使う
export const selectCanSendToOpenChat = (state: AgentWidgetState): boolean => selectOpenRunningTurnId(state) === null

// チャットの会話の対象の view の id。指示を送るたびに本文の page に入れる。対象がなければ null
export const selectChatTargetViewId = (state: AgentWidgetState, agentSessionId: string): string | null =>
  chatTargetOf(state.targets, agentSessionId)
